import { FileResponse, IFileResponse, IWorldStreamResource, UserTinyResource } from '@api/client';
import { FileTypes } from '@api/extended-client';
import { ParsedUser } from '@services/auth.service';
import { mapFileActionsToStream } from '@utils/file-actions-to-stream';
import { getFilePreviewUrl } from '@utils/file-preview-url';
import { isImageByExtension, isVideoByExtension } from '@utils/media-file.helpers';

export interface ProfileMediaPlaylistResult {
  streams: IWorldStreamResource[];
  targetFileId: number | null;
}

export function buildProfileMediaPlaylist(
  files: IFileResponse[],
  clickedFile: IFileResponse,
  parsedUser: ParsedUser | null
): ProfileMediaPlaylistResult {
  const streams: IWorldStreamResource[] = [];
  let targetFileId: number | null = null;
  const clickedId = typeof clickedFile?.id === 'number' ? clickedFile.id : null;
  let sequence = 1;

  for (const file of files) {
    if (!isReadyMediaFile(file)) {
      continue;
    }
    const fileId = typeof file.id === 'number' ? file.id : null;
    if (fileId == null || fileId <= 0) {
      continue;
    }
    const fileUrl = resolveProfileFileUrl(file);
    if (!fileUrl) {
      continue;
    }
    const stream = createProfileFileStream(file, parsedUser, sequence, fileId, fileUrl);
    streams.push(stream);
    sequence += 1;

    const isTarget = (clickedId != null && file.id === clickedId) || file === clickedFile;
    if (isTarget && targetFileId == null) {
      targetFileId = fileId;
    }
  }

  return { streams, targetFileId };
}

function createProfileFileStream(
  file: IFileResponse,
  parsedUser: ParsedUser | null,
  videoId: number,
  fileId: number,
  fileUrl: string
): IWorldStreamResource {
  const { actions, myActions } = mapFileActionsToStream(file);
  const user = resolveUserTiny(parsedUser);
  const fileTitle = typeof file.title === 'string' ? file.title.trim() : '';
  const fileName = typeof file.name === 'string' ? file.name.trim() : '';
  const description = typeof file.description === 'string' ? file.description.trim() : '';
  const authorId = typeof file.user_id === 'number'
    ? file.user_id
    : (typeof parsedUser?.id === 'number' ? parsedUser.id : undefined);

  const stream: IWorldStreamResource = {
    video_id: videoId,
    author_id: authorId,
    live: false,
    name: fileTitle.length > 0 ? fileTitle : (fileName.length > 0 ? fileName : undefined),
    description: description.length > 0 ? description : undefined,
    user,
    actions: actions ?? undefined,
    my_actions: myActions ?? undefined,
    file_id: fileId,
    file_url: fileUrl,
    file,
  };

  return stream;
}

function resolveUserTiny(parsedUser: ParsedUser | null): UserTinyResource | undefined {
  if (!parsedUser) {
    return undefined;
  }
  const avatar = parsedUser.avatar ? new FileResponse(parsedUser.avatar) : undefined;
  const name = parsedUser.name ?? undefined;
  const surname = parsedUser.surname ?? undefined;
  const official = parsedUser.official ?? undefined;
  return new UserTinyResource({ name, surname, official, avatar });
}

function normalizeTypeName(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim().toLowerCase();
  return trimmed.length > 0 ? trimmed : null;
}

function isReadyMediaFile(file: IFileResponse | null | undefined): boolean {
  return isReadyVideoFile(file) || isReadyImageFile(file);
}

function isReadyVideoFile(file: IFileResponse | null | undefined): boolean {
  if (!file) {
    return false;
  }
  const typeValue = file.type;
  if (typeValue) {
    if (typeValue.id === FileTypes.TYPE_VIDEO) {
      return resolveProfileVideoUrl(file) !== null;
    }
    const normalizedName = normalizeTypeName(typeValue.name);
    if (normalizedName === 'video' || normalizedName?.startsWith('video/')) {
      return resolveProfileVideoUrl(file) !== null;
    }
  }

  const url = typeof file.url === 'string' ? file.url : '';
  const name = typeof file.name === 'string' ? file.name : '';
  if (isVideoByExtension(url) || isVideoByExtension(name)) {
    return resolveProfileVideoUrl(file) !== null;
  }
  return false;
}

function isReadyImageFile(file: IFileResponse | null | undefined): boolean {
  if (!file) {
    return false;
  }
  const typeValue = file.type;
  if (typeValue) {
    if (typeValue.id === FileTypes.TYPE_IMAGE) {
      return resolveProfileImageUrl(file) !== null;
    }
    const normalizedName = normalizeTypeName(typeValue.name);
    if (normalizedName === 'image' || normalizedName?.startsWith('image/')) {
      return resolveProfileImageUrl(file) !== null;
    }
  }

  const url = typeof file.url === 'string' ? file.url : '';
  const name = typeof file.name === 'string' ? file.name : '';
  if (isImageByExtension(url) || isImageByExtension(name)) {
    return resolveProfileImageUrl(file) !== null;
  }
  return false;
}

function resolveProfileFileUrl(file: IFileResponse): string | null {
  return isReadyVideoFile(file) ? resolveProfileVideoUrl(file) : resolveProfileImageUrl(file);
}

function resolveProfileVideoUrl(file: IFileResponse): string | null {
  const directUrl = typeof file.url === 'string' ? file.url.trim() : '';
  if (directUrl.length > 0) {
    return directUrl;
  }
  const originUrl = typeof file.origin?.url === 'string' ? file.origin.url.trim() : '';
  return originUrl.length > 0 ? originUrl : null;
}

function resolveProfileImageUrl(file: IFileResponse): string | null {
  const fallbackUrl = typeof file.url === 'string' ? file.url.trim() : null;
  const previewUrl = getFilePreviewUrl(file, { fallbackUrl });
  if (typeof previewUrl === 'string' && previewUrl.trim().length > 0) {
    return previewUrl.trim();
  }
  return fallbackUrl && fallbackUrl.length > 0 ? fallbackUrl : null;
}
